import { getLogger } from 'app-components/utils/core';
import { Map as ImmutableMap, List, type RecordOf } from 'immutable';

import { institutionsTypes } from '.';
import * as types from './institutionsTypes';

const log = getLogger('app-components/flux/institutions/institutionsTransformers');

// QCS sends us institution data in a different format, we transform that below.
// This type should never need to be exported, finished transformed types are in types file
type QCSInstitution = {
  ofx: any;
  fds: any;
  fdp: any;
  loginUrl?: string;
  isEwcLive?: boolean;
  channels: any[];
} & types.Institution;

export const transformResponseToInstitutions = (response?: { data?: types.Institution[] }) =>
  response?.data?.map((institution) => transformApiDataToInstitution(institution as QCSInstitution));

export const transformResponseToInstitution = ({ data }: { data: types.Institution }) =>
  transformApiDataToInstitution(data as QCSInstitution);

export const transformSearchResponseToInstitutions = (response?: { data?: types.Institution[] }) =>
  List(response?.data?.map(({ id, name, url }) => types.mkInstitution({ id, name, url })));

const transformApiDataToInstitution = (data: QCSInstitution) =>
  types.mkInstitution({
    id: data.id,
    name: data.name,
    url: data.url,

    logo: data.logo,
    logoUrl: data.logoUrl,
    logoStyle: data.logoStyle,

    aggregator: data.aggregator,
    ofx: data.ofx
      ? types.mkInstitutionOfxData({
          bid: data.ofx.bid,
          pid: data.ofx.pid,
          name: data.ofx.name,
          contact_url: data.ofx.contact ? data.ofx.contact.url : '',
          contact_phone: data.ofx.contact ? data.ofx.contact.phone : '',
          registraction_url: data.ofx.dcRegistrationUrl,
        })
      : null,
    fds: data.fds
      ? types.mkInstitutionFdsData({
          id: data.fds.id,
          name: data.fds.name,

          contact_url: data.fds.contact ? data.fds.contact.url : '',
          contact_phone: data.fds.contact ? data.fds.contact.phone : '',
          contact_email: data.fds.contact ? data.fds.contact.email : '',

          login_url: data.fds.loginUrl,
          channelData: transformApiChannelDataToChannel(data.fds, 'FDP_DEFAULT', data.fds.form?.fieldEncryptionKey),

          ewcLive: data.fds ? data.fds?.isEwcLive : false,
        })
      : null,
    fdp: data.fdp
      ? types.mkInstitutionFdpData({
          id: data.fdp.id,
          name: data.fdp.name,

          contact_url: data.fdp.contact ? data.fdp.contact.url : '',
          contact_phone: data.fdp.contact ? data.fdp.contact.phone : '',
          contact_email: data.fdp.contact ? data.fdp.contact.email : '',

          login_url: data.fdp.loginUrl,
          channelData: transformApiChannelDataToChannel(data.fdp, undefined, data.fdp.fieldEncryptionKey),
          channelDataByType: data.fdp.channels?.length
            ? ImmutableMap<string, RecordOf<types.InstitutionChannelData>>(
                data.fdp.channels
                  .filter((channel: any) => channel)
                  .map((channel: any) => [
                    channel.channelType,
                    transformApiChannelDataToChannel(channel, undefined, data.fdp.fieldEncryptionKey),
                  ]),
              )
            : undefined,
        })
      : null,
    plaid: data.plaid
      ? types.mkInstitutionPlaidData({
          id: data.plaid.id,
          name: data.plaid.name,
        })
      : null,
    finicity: data.finicity
      ? types.mkInstitutionFinicityData({
          id: data.finicity.id,
          name: data.finicity.name,
        })
      : null,
    fiIssue: data.fiIssue ? institutionsTypes.mkFiIssue(data.fiIssue) : null,
  });

type QCSResChannelData = {
  channelType: string;
  form: {
    signature: string;
    signatureAlgorithm: string;
    fields: List<types.InstitutionFormField & { cpId: string }>;
  };
} & institutionsTypes.InstitutionChannelData;

const transformApiChannelDataToChannel = (
  channelData: QCSResChannelData,
  defaultChannel?: string,
  encryptionData?: any,
) => {
  if (!channelData) {
    log.warn('empty channelData passed to API transformer!');
    return undefined;
  }
  return types.mkInstitutionChannelData({
    type: channelData.channelType || defaultChannel,
    authType: channelData.authType,
    formFields: channelData.form
      ? List<RecordOf<types.InstitutionFormField>>(
          channelData.form.fields.map((field: any) =>
            types.mkInstitutionFormField({
              key: field.cpId,
              name: field.name,
              type: field.type,
              displayOrder: field.displayOrder,
              displayLabel: field.displayLabel,
              helpText: field.helpText,
              maxLength: field.maxLength,
              encrypt: field.encrypt,
            }),
          ),
        )
      : null,
    formEncryption: channelData.form?.signature ? types.mkInstitutionFormEncryption(encryptionData) : undefined,
    formSignature: channelData.form?.signature,
    formSignatureAlgorithm: channelData.form?.signatureAlgorithm,
    partnerAuth: channelData.partnerAuth
      ? types.mkInstitutionPartnerAuth({
          intuitProperty: channelData.partnerAuth.intuitProperty,
          offeringRedirectUri: channelData.partnerAuth.offeringRedirectUri,
          partnerAuthUrl: channelData.partnerAuth.partnerAuthUrl,
          partnerUid: channelData.partnerAuth.partnerUid,
          fields: List<RecordOf<types.InstitutionFormField>>(
            channelData.partnerAuth.fields.map((field: any) =>
              types.mkInstitutionFormField({
                key: field.cpId,
                name: field.name,
                type: field.type,
                displayOrder: field.displayOrder,
                displayLabel: field.displayLabel,
                helpText: field.helpText,
                maxLength: field.maxLength,
                encrypt: field.encrypt,
              }),
            ),
          ),
        })
      : null,
  });
};
